/**
 * What is in the system, in totals.
 *
 * Kept, but not the landing screen: the app opens on the sports list, and
 * anybody working a match day starts at Today. Nobody opens a data-entry tool
 * to find out how many venues exist — they open it because something is
 * starting. This is the page for when you do want the totals.
 */

import { IconCalendar, IconDatabase, IconMapPin, IconTrophy } from "@/components/Icons";
import { PageHead, Problem, Section, Stat, StatGrid } from "@/components/kit";
import { SportsTable } from "@/components/SportsTable";
import { listVenues } from "@/lib/api/client";
import { loadSportRows } from "@/lib/sportRows";
import { useApi } from "@/spa/useApi";

export function DashboardPage() {
  const { data, loading, error } = useApi(
    () => Promise.all([loadSportRows(), listVenues()]),
    [],
    "failed to load",
  );

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  // Totals of nothing are zeroes, which read as real answers. Until the first
  // load lands the page shows its heading and nothing else.
  if (!data) {
    return <PageHead title="Dashboard" note={loading ? "Loading…" : ""} />;
  }

  const [rows, venues] = data;
  const published = rows.filter((r) => r.published !== null).length;
  const masters = rows.reduce((total, r) => total + r.masters, 0);
  const fixtures = rows.reduce((total, r) => total + r.fixtures, 0);

  return (
    <>
      <PageHead title="Dashboard" note="Everything in the system, in totals." />

      <StatGrid>
        <Stat
          label="Sports"
          value={rows.length}
          note={`${published} published`}
          icon={<IconTrophy size={15} />}
          href="/sports"
        />
        <Stat
          label="Masters"
          value={masters}
          note="configured across sports"
          icon={<IconDatabase size={15} />}
        />
        <Stat
          label="Fixtures"
          value={fixtures}
          note="across all sports"
          icon={<IconCalendar size={15} />}
          href="/fixtures"
        />
        <Stat
          label="Venues"
          value={venues.length}
          note="shared across sports"
          icon={<IconMapPin size={15} />}
          href="/commons"
        />
      </StatGrid>

      <Section title="Sports">
        <SportsTable rows={rows} />
      </Section>
    </>
  );
}
